import React from 'react';
import { colors } from '../styles/tokens';
import { useEsMovil } from '../hooks/useEsMovil';
import { previousMonthRange, previousWeekRange } from '../utils/aggregate';
import { currentMonthRange } from '../utils/meta';
import {
  BorradorPeriodo,
  FECHA_MINIMA,
  hoyIso,
  Periodo,
  PERIODO_VIVO,
  periodoRango,
} from '../utils/periodo';

export interface PanelFiltrosProps {
  /** `data-testid` del panel (`filtros-global` en Global y Presupuesto, `panel-tienda` en Tienda). */
  testid: string;
  /** Rótulo de la zona de ámbito. */
  tituloAmbito: string;
  /** Los combos de la zona ÁMBITO (cada vista pone los suyos). */
  ambito: React.ReactNode;
  /** Base flex de la zona ÁMBITO en escritorio (ver el comentario de `Filtros`). */
  baseAmbito?: string;
  /** Algo junto al rótulo PERIODO (Tienda: «actualizado HH:MM»). */
  extraPeriodo?: React.ReactNode;
  borradorPeriodo: BorradorPeriodo;
  onBorradorPeriodo: (b: BorradorPeriodo) => void;
  /**
   * Atajo pulsado. Los atajos APLICAN AL INSTANTE (decisión del usuario,
   * 12-oct): quien los usa no está tecleando y no hay nada a medio escribir.
   */
  onAtajo: (p: Periodo) => void;
  /** El borrador (todo el panel) difiere de lo aplicado. */
  sucio: boolean;
  /** Problema del borrador en español, o `null` si se puede aplicar. */
  error: string | null;
  onAplicar: () => void;
  /** Presupuesto: añade «Mes en curso» y deja elegir fechas futuras. */
  rangosDeMeta?: boolean;
}

/** `true` si `b` es exactamente el rango `r` (y no el vivo). */
function esRango(b: BorradorPeriodo, r: { desde: string; hasta: string }): boolean {
  return !b.vivo && b.desde === r.desde && b.hasta === r.hasta;
}

/**
 * EL PANEL DE FILTROS DEL TABLERO, el mismo en Global, Tienda y Presupuesto
 * (SPEC `2026-10-12-tableros-homologados.md`, «Panel de filtros»): zona ÁMBITO
 * (la pone cada vista), zona PERIODO y el pie con «Aplicar».
 *
 * ⚠️ BORRADOR CONTRA APLICADO. Hasta el 12-oct cada tecla en una fecha
 * disparaba consultas: Chrome entrega un valor completo con cada dígito del año
 * (0002 → 0020 → 0202 → 2026), así que reteclear el año eran 12 peticiones con
 * años absurdos — y el 11-oct, 38 respuestas 500 en producción. Ahora el panel
 * sólo edita un borrador; las consultas leen lo APLICADO, que cambia con
 * «Aplicar», con Enter en una fecha o con un atajo.
 *
 * ⚠️ LÍMITES FIJOS. `min`/`max` de las fechas no dependen del otro campo, y una
 * fecha no se copia a la otra mientras se teclea: cualquiera de las dos cosas
 * reiniciaba el búfer del año en Chrome y dejaba 0026 donde se tecleó 2026.
 * La coherencia (desde ≤ hasta, ≤ hoy, ≤ 366 días) se valida aquí, en línea, y
 * otra vez en la API.
 */
const PanelFiltros: React.FC<PanelFiltrosProps> = ({
  testid,
  tituloAmbito,
  ambito,
  baseAmbito = '1 1 360px',
  extraPeriodo,
  borradorPeriodo,
  onBorradorPeriodo,
  onAtajo,
  sucio,
  error,
  onAplicar,
  rangosDeMeta = false,
}) => {
  const esMovil = useEsMovil();
  const hoy = hoyIso();
  // Presupuesto mira el mes en curso hasta fin de mes (y la API lo permite en
  // /meta); el resto del tablero no puede pedir días que no han pasado.
  const max = rangosDeMeta ? `${Number(hoy.slice(0, 4)) + 1}-12-31` : hoy;
  const puedeAplicar = sucio && !error;

  const b = borradorPeriodo;
  // Un rango {hoy, hoy} se aplica como el vivo (`periodoDeBorrador`): la pill
  // lo dice igual.
  const esHoy = b.vivo || (b.desde === hoy && b.hasta === hoy);
  const semana = previousWeekRange();
  const mes = previousMonthRange();
  const mesEnCurso = currentMonthRange();

  const aplicarSiSePuede = () => {
    if (puedeAplicar) onAplicar();
  };

  const alTeclear = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      aplicarSiSePuede();
    }
  };

  const groupLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    // 10 px no se lee en un teléfono; en móvil sube a 11, el piso que nos
    // pusimos para etiquetas.
    fontSize: esMovil ? 11 : 10,
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    color: '#a2a7b2',
    fontWeight: 800,
    marginBottom: 8,
  };

  // Desde/Hasta usan el MISMO label (10px) + valor (14px) que el `Combo` flat
  // para que ÁMBITO y PERIODO compartan diseño y línea base. En móvil la
  // etiqueta sale ARRIBA del campo: el input tiene que medir 44 px él solo.
  const flatLabelStyle: React.CSSProperties = {
    fontSize: 10,
    letterSpacing: '.09em',
    textTransform: 'uppercase',
    color: '#9298a3',
    fontWeight: 800,
  };
  const dateLabelStyle: React.CSSProperties = esMovil
    ? { ...flatLabelStyle, fontSize: 11, display: 'block', marginBottom: 6 }
    : flatLabelStyle;

  const flatDateInputStyle: React.CSSProperties = {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontWeight: 700,
    fontSize: 14,
    color: '#14171d',
    fontVariantNumeric: 'tabular-nums',
    colorScheme: 'light',
    padding: 0,
    marginTop: 3,
    width: '100%',
  };
  // En móvil: caja propia de 44 px y ancho completo. `boxSizing` es obligatorio
  // con `width: 100%` más padding y borde, o el campo se sale de la rejilla.
  const dateInputStyle: React.CSSProperties = esMovil
    ? {
        ...flatDateInputStyle,
        fontSize: 15,
        marginTop: 0,
        minHeight: 44,
        padding: '0 10px',
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        boxSizing: 'border-box',
      }
    : flatDateInputStyle;

  const dateCellStyle = (index: number): React.CSSProperties =>
    esMovil
      ? { minWidth: 0 }
      : {
          borderLeft: index === 0 ? undefined : `1px solid ${colors.rowBorder}`,
          padding: index === 0 ? '0 14px 0 0' : '0 14px',
          minWidth: 112,
        };

  const segPillStyle = (active: boolean): React.CSSProperties => ({
    border: 'none',
    borderRadius: 8,
    // En móvil 44 px de alto y el texto puede envolverse (columnas estrechas).
    padding: esMovil ? '10px 8px' : '7px 13px',
    minHeight: esMovil ? 44 : undefined,
    fontFamily: 'inherit',
    fontSize: esMovil ? 13 : 12.5,
    fontWeight: active ? 800 : 700,
    cursor: 'pointer',
    whiteSpace: esMovil ? 'normal' : 'nowrap',
    background: active ? '#22349b' : 'transparent',
    color: active ? '#fff' : colors.texto.secondary,
  });

  const filterIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a2a7b2" strokeWidth={2.4} strokeLinecap="round">
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
  const calIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a2a7b2" strokeWidth={2.2} strokeLinecap="round">
      <rect x="3" y="4.5" width="18" height="17" rx="2.5" />
      <path d="M3 9h18M8 2.5v4M16 2.5v4" />
    </svg>
  );

  return (
    <div
      data-testid={testid}
      style={{
        display: 'flex',
        // En móvil las zonas se apilan: ÁMBITO y PERIODO no caben lado a lado
        // en 390 px.
        flexDirection: esMovil ? 'column' : 'row',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        marginTop: esMovil ? 12 : 16,
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: esMovil ? '13px 14px' : '13px 16px',
        boxShadow: '0 1px 2px rgba(20,28,54,.05), 0 18px 42px -32px rgba(20,28,54,.55)',
        position: 'relative',
        zIndex: 20,
      }}
    >
      {/* ===== Zona A — ÁMBITO =====
          Ojo con la base flex: en dirección columna se lee como ALTO, no como
          ancho; en móvil la zona pasa a `0 0 auto` y se mide sola. */}
      <div style={{ flex: esMovil ? '0 0 auto' : baseAmbito, minWidth: 0 }}>
        <div style={groupLabelStyle}>
          {filterIcon}
          <span>{tituloAmbito}</span>
        </div>
        {ambito}
      </div>

      {/* Divisor entre zonas: en móvil se acuesta. */}
      <div
        style={
          esMovil
            ? { height: 1, width: '100%', background: colors.rowBorder, margin: '14px 0' }
            : { width: 1, background: colors.rowBorder, alignSelf: 'stretch', margin: '2px 0' }
        }
      />

      {/* ===== Zona B — PERIODO ===== */}
      <div style={{ paddingLeft: esMovil ? 0 : 16, flex: '0 0 auto', minWidth: 0 }}>
        <div style={{ ...groupLabelStyle, justifyContent: 'space-between', gap: 10 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {calIcon}
            <span>Periodo</span>
          </span>
          {extraPeriodo}
        </div>
        <div>
          <div
            style={
              esMovil
                ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }
                : { display: 'flex', alignItems: 'flex-end' }
            }
          >
            <div style={dateCellStyle(0)}>
              <label htmlFor={`${testid}-desde`} style={dateLabelStyle}>
                Desde
              </label>
              {/* `min`/`max` FIJOS (ver el comentario del componente). */}
              <input
                id={`${testid}-desde`}
                data-testid="filtros-fecha-desde"
                type="date"
                value={b.desde}
                min={FECHA_MINIMA}
                max={max}
                onChange={(e) => onBorradorPeriodo({ vivo: false, desde: e.target.value, hasta: b.hasta })}
                onKeyDown={alTeclear}
                aria-invalid={sucio && !!error}
                style={dateInputStyle}
              />
            </div>
            <div style={dateCellStyle(1)}>
              <label htmlFor={`${testid}-hasta`} style={dateLabelStyle}>
                Hasta
              </label>
              <input
                id={`${testid}-hasta`}
                data-testid="filtros-fecha-hasta"
                type="date"
                value={b.hasta}
                min={FECHA_MINIMA}
                max={max}
                onChange={(e) => onBorradorPeriodo({ vivo: false, desde: b.desde, hasta: e.target.value })}
                onKeyDown={alTeclear}
                aria-invalid={sucio && !!error}
                style={dateInputStyle}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ===== Pie: validación + atajos + «Cambios sin aplicar» + Aplicar =====
          A todo el ancho del panel porque «Aplicar» aplica TODO el panel
          (ámbito, estatus, colaborador y fechas), no sólo las fechas. */}
      <div
        style={{
          flexBasis: '100%',
          display: 'flex',
          flexDirection: esMovil ? 'column' : 'row',
          alignItems: esMovil ? 'stretch' : 'center',
          justifyContent: 'flex-end',
          gap: esMovil ? 8 : 12,
          marginTop: 12,
          paddingTop: 10,
          borderTop: `1px solid ${colors.rowBorder}`,
        }}
      >
        <span
          data-testid="error-periodo"
          aria-live="polite"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            fontWeight: 700,
            color: colors.rojo,
            // Sin error el hueco se conserva vacío: así el botón no salta de
            // sitio cada vez que aparece o desaparece el mensaje.
            visibility: sucio && error ? 'visible' : 'hidden',
          }}
        >
          {sucio && error ? error : ''}
        </span>
        {/* Atajos: el mismo texto y la misma forma en las tres superficies, y
            aplican al instante. Viven en el pie, junto a «Aplicar», y no al lado
            de las fechas: con el texto completo («Semana anterior», «Mes
            anterior») la zona PERIODO crecía hasta ~720 px y a 1440 px los cinco
            combos de ÁMBITO quedaban cortados («Todos l…»). La pill activa se
            resuelve por VALOR contra el borrador. */}
        <div
          role="group"
          aria-label="Periodo rápido"
          style={{
            display: esMovil ? 'grid' : 'inline-flex',
            // En teléfono, las columnas dividen a los atajos: 3 en una fila, o
            // 2 × 2 con «Mes en curso». Con 2 columnas fijas el tercero quedaba
            // solo en su fila.
            gridTemplateColumns: esMovil ? `repeat(${rangosDeMeta ? 2 : 3}, minmax(0, 1fr))` : undefined,
            gap: esMovil ? 6 : undefined,
            width: esMovil ? '100%' : undefined,
            boxSizing: esMovil ? 'border-box' : undefined,
            background: '#eef0f5',
            border: '1px solid #e2e5ee',
            borderRadius: 11,
            padding: 3,
            flex: 'none',
          }}
        >
          <button
            type="button"
            data-testid="quick-hoy"
            aria-pressed={esHoy}
            style={segPillStyle(esHoy)}
            onClick={() => onAtajo(PERIODO_VIVO)}
          >
            Hoy (en vivo)
          </button>
          {rangosDeMeta && (
            <button
              type="button"
              data-testid="quick-mes-en-curso"
              aria-pressed={esRango(b, mesEnCurso)}
              style={segPillStyle(esRango(b, mesEnCurso))}
              onClick={() => onAtajo(periodoRango(mesEnCurso))}
            >
              Mes en curso
            </button>
          )}
          <button
            type="button"
            data-testid="quick-semana-anterior"
            aria-pressed={esRango(b, semana)}
            style={segPillStyle(esRango(b, semana))}
            onClick={() => onAtajo(periodoRango(semana))}
          >
            Semana anterior
          </button>
          <button
            type="button"
            data-testid="quick-mes-anterior"
            aria-pressed={esRango(b, mes)}
            style={segPillStyle(esRango(b, mes))}
            onClick={() => onAtajo(periodoRango(mes))}
          >
            Mes anterior
          </button>
        </div>
        {sucio && (
          <span
            data-testid="cambios-sin-aplicar"
            style={{
              alignSelf: esMovil ? 'flex-start' : undefined,
              fontSize: 12,
              fontWeight: 700,
              color: '#92400e',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: 999,
              padding: '3px 11px',
              whiteSpace: 'nowrap',
            }}
          >
            Cambios sin aplicar
          </span>
        )}
        <button
          type="button"
          data-testid="aplicar-filtros"
          onClick={aplicarSiSePuede}
          disabled={!puedeAplicar}
          title={
            !sucio ? 'No hay cambios que aplicar' : error ? error : 'Aplicar los filtros (o Enter en una fecha)'
          }
          style={{
            border: 'none',
            borderRadius: 10,
            padding: esMovil ? '0 18px' : '8px 22px',
            minHeight: esMovil ? 44 : undefined,
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 800,
            cursor: puedeAplicar ? 'pointer' : 'not-allowed',
            background: puedeAplicar ? '#22349b' : '#e2e5ee',
            color: puedeAplicar ? '#fff' : '#9298a3',
            boxShadow: puedeAplicar ? '0 4px 12px -4px rgba(34,52,155,.6)' : 'none',
          }}
        >
          Aplicar
        </button>
      </div>
    </div>
  );
};

export default PanelFiltros;
